import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface NavItem {
  label: string;
  path: string;
}

interface NavGroup {
  label: string;
  module: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Administration',
    module: 'Administration',
    items: [
      { label: 'Master', path: '/admin/master' },
      { label: 'Access Rights', path: '/admin/access-rights' },
      { label: 'Create User', path: '/admin/users/new' },
      { label: 'User List', path: '/admin/users' },
      { label: 'Employee Access', path: '/admin/employee-access' },
      { label: 'Email & 2FA Settings', path: '/admin/email-settings' },
      { label: 'Approver Settings', path: '/admin/approver-settings' },
    ],
  },
  {
    label: 'Human Resources',
    module: 'MAVIS-HR',
    items: [
      { label: 'Dashboard', path: '/hr/dashboard' },
      { label: 'Employees', path: '/hr/employees' },
      { label: 'Employee Personal Details', path: '/hr/personal-details' },
      { label: 'Training', path: '/hr/training' },
      { label: 'Feedback', path: '/hr/feedback' },
      { label: 'Meeting Rooms', path: '/hr/meeting-rooms' },
      { label: 'Organization Chart', path: '/hr/org-chart' },
      { label: 'Staff Personal Approve/Reject', path: '/hr/staff-personal-changes' },
      { label: 'Letter Editor', path: '/hr/letters' },
      { label: 'Policies', path: '/hr/policies' },
      { label: 'Memos', path: '/hr/memos' },
      { label: 'Candidates', path: '/hr/candidates' },
      { label: 'Onboarding / Offboarding Setup', path: '/hr/onboarding-setup' },
    ],
  },
  {
    label: 'Time Management',
    module: 'MAVIS-TMS',
    items: [
      { label: 'Dashboard', path: '/tms/dashboard' },
      { label: 'Shifts', path: '/tms/shifts' },
      { label: 'Auto Shift', path: '/tms/auto-shifts' },
      { label: 'Duty Roster', path: '/tms/duty-rosters' },
      { label: 'Attendance', path: '/tms/attendance' },
      { label: 'Branches', path: '/tms/branches' },
      { label: 'System Parameters', path: '/tms/system-parameters' },
      { label: 'Reader Status', path: '/tms/readers' },
      { label: 'Employee Schedule Import/Export', path: '/tms/employee-schedule-import' },
      { label: 'Branch Schedule', path: '/tms/branch-schedule' },
      { label: 'Allowances', path: '/tms/allowances' },
      { label: 'Update by Individual', path: '/tms/update-by-individual' },
      { label: 'Update by Department', path: '/tms/update-by-department' },
      { label: 'Reprocess / Recalculate', path: '/tms/reprocess-recalculate' },
      { label: 'TMS Reports', path: '/tms/reports' },
    ],
  },
  {
    label: 'Leave Management',
    module: 'MAVIS-Leave',
    items: [
      { label: 'Dashboard', path: '/lms/dashboard' },
      { label: 'System Settings', path: '/lms/system-settings' },
      { label: 'Medical Claim Settings', path: '/lms/masters/medical-claim-settings' },
      { label: 'Exclude Days', path: '/lms/masters/exclude-days' },
      { label: 'Off-in-Lieu', path: '/lms/oil-credits' },
      { label: 'Credit Annual Leave', path: '/lms/leave-adjustments' },
      { label: 'Block Annual Leave', path: '/lms/masters/block-al' },
      { label: 'Maternity / Paternity', path: '/lms/maternity-paternity' },
      { label: 'Leave Eligibility', path: '/lms/leave-eligibility' },
      { label: 'Leave Types', path: '/lms/leave-types' },
      { label: 'Leave Groups', path: '/lms/leave-groups' },
      { label: 'Leave Categories', path: '/lms/leave-categories' },
      { label: 'Leave Applications', path: '/lms/leave-applications' },
      { label: 'Apply on Behalf', path: '/lms/apply-on-behalf' },
      { label: 'Leave Recalculate', path: '/lms/leave-recalculate' },
      { label: 'Year-End Process', path: '/lms/year-end' },
      { label: 'Holidays', path: '/lms/holidays' },
      { label: 'Leave Reports', path: '/lms/reports' },
      { label: 'FWA Settings', path: '/lms/fwa/settings' },
      { label: 'FWA Applications', path: '/lms/fwa/applications' },
    ],
  },
  {
    label: 'Claims',
    module: 'MAVIS-Claim',
    items: [
      { label: 'Utility Settings', path: '/ecs/settings' },
      { label: 'Claim Date Ranges', path: '/ecs/masters/claim-date-ranges' },
      { label: 'Claim Group/Claim/Sub-Claim', path: '/ecs/masters/claim-groups' },
      { label: 'Claim Limits', path: '/ecs/masters/claim-limits' },
      { label: 'Claim Applications', path: '/ecs/claims' },
      { label: 'Claim Reports', path: '/ecs/reports' },
    ],
  },
  {
    label: 'Performance Appraisal',
    module: 'MAVIS-Appraisal',
    items: [
      { label: 'Settings', path: '/pas/settings' },
      { label: 'Rating Groups', path: '/pas/masters/rating-groups' },
      { label: 'Grades', path: '/pas/masters/grades' },
      { label: 'Appraisal Templates', path: '/pas/masters/templates' },
      { label: 'Assign Appraisal', path: '/pas/assign' },
      { label: 'Approve Appraisal', path: '/pas/approve' },
      { label: 'Publish Appraisal', path: '/pas/publish' },
      { label: 'Appraisal Reports', path: '/pas/reports' },
    ],
  },
  {
    label: 'Payroll',
    module: 'MAVIS-Pay',
    items: [
      { label: 'Dashboard', path: '/payroll/dashboard' },
      { label: 'System Settings', path: '/payroll/system-settings' },
      { label: 'Pay Day Group', path: '/payroll/pay-day-groups' },
      { label: 'Sections', path: '/masters/sections' },
      { label: 'Designation Groups', path: '/masters/designation-groups' },
      { label: 'FWL', path: '/payroll/masters/fwl' },
      { label: 'Daily Rate', path: '/payroll/masters/daily-rates' },
      { label: 'Holiday Group', path: '/payroll/masters/holiday-groups' },
      { label: 'Allowances / Deductions', path: '/payroll/masters/allowances-deductions' },
      { label: 'Salary Structures', path: '/payroll/structures' },
      { label: 'Payroll Runs & Payslips', path: '/payroll/payslips' },
      { label: 'Bank Submission', path: '/payroll/bank-submission' },
      { label: 'CPF Submission', path: '/payroll/cpf-submission' },
      { label: 'Reports', path: '/payroll/reports' },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { hasNodeAccess } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  // ADM-07d: filter individual nav items by their own node permission (falls
  // back to the group's module-level permission when no per-node override is
  // set — see AuthContext's hasNodeAccess), not just the whole group at once.
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasNodeAccess(item.path)),
  })).filter((group) => group.items.length > 0);
  const [openGroup, setOpenGroup] = useState<string | null>(() => {
    // auto-expand the group containing the current route
    const active = NAV_GROUPS.find((group) =>
      group.items.some((item) => location.pathname.startsWith(item.path))
    );
    return active ? active.label : null;
  });

  const toggleGroup = (label: string) => {
    setOpenGroup((prev) => (prev === label ? null : label));
  };

  return (
    <aside
      className={`bg-gray-900 text-gray-200 flex flex-col shrink-0 transition-all ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
        {!collapsed && <span className="font-bold text-white text-lg">MAVIS HRMS</span>}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-gray-400 hover:text-white text-sm"
          aria-label="Toggle sidebar"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        <Link
          to="/dashboard"
          className={`block border-l-4 px-4 py-2 text-sm font-medium hover:bg-gray-800 ${
            location.pathname === '/dashboard'
              ? 'border-blue-400 bg-blue-600 text-white font-semibold'
              : 'border-transparent text-gray-300'
          }`}
        >
          {collapsed ? '⌂' : 'Dashboard'}
        </Link>
        <Link
          to="/ess"
          className={`block border-l-4 px-4 py-2 text-sm font-medium hover:bg-gray-800 ${
            location.pathname === '/ess'
              ? 'border-blue-400 bg-blue-600 text-white font-semibold'
              : 'border-transparent text-gray-300'
          }`}
        >
          {collapsed ? '☺' : 'My Self-Service'}
        </Link>

        {visibleGroups.map((group) => (
          <div key={group.label} className="mt-1">
            <button
              onClick={() => toggleGroup(group.label)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-200"
            >
              {!collapsed && <span>{group.label}</span>}
              {!collapsed && <span>{openGroup === group.label ? '▾' : '▸'}</span>}
            </button>
            {(collapsed || openGroup === group.label) &&
              group.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block border-l-4 px-4 py-2 text-sm hover:bg-gray-800 ${
                    location.pathname === item.path
                      ? 'border-blue-400 bg-blue-600 text-white font-semibold'
                      : 'border-transparent text-gray-300'
                  } ${collapsed ? 'pl-4' : 'pl-6'}`}
                  title={item.label}
                >
                  {collapsed ? item.label.charAt(0) : item.label}
                </Link>
              ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
